import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { setAuthReset } from '../../redux/auth/authSlice';
import { hasRole } from '../../utils/token';
import { Pill, IconButton } from '../ui';
import {
    Shell, Sidebar, Brand, BrandLogo, BrandName,
    NavGroupTitle, NavItem, SidebarFooter, UserCard, Avatar, UserMeta,
    Main, Topbar, TopbarSpacer, Content,
} from './appShell.styled';

// Navigation groupée par usage. `roles: null` = visible pour tous.
const NAV_GROUPS = [
    {
        title: 'Recherche',
        items: [
            { to: '/Users', label: 'Utilisateurs', icon: 'bx-user', roles: null },
            { to: '/AdvancedSearch', label: 'Recherche avancée', icon: 'bx-filter-alt', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS', 'Users.Basic'] },
            { to: '/Diff', label: 'Diff', icon: 'bx-git-compare', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
        ],
    },
    {
        title: 'Analyse',
        items: [
            { to: '/Dashboard', label: 'Dashboard', icon: 'bx-grid-alt', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
            { to: '/AiQuery', label: 'Requête IA', icon: 'bx-bot', roles: ['Users.Admin', 'Users.RSSI'] },
        ],
    },
    {
        title: 'Sécurité',
        items: [
            // `end` : sans lui, NavLink marquerait aussi ce lien actif sur
            // /Security/Vulnerabilities, qui commence par le même chemin.
            { to: '/Security', label: "Vue d'ensemble", icon: 'bx-shield-alt-2', end: true, roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
            { to: '/Security/Vulnerabilities', label: 'Vulnérabilités', icon: 'bx-bug', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
            { to: '/Security/Devices', label: 'Appareils', icon: 'bx-laptop', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
            { to: '/Security/Compliance', label: 'Conformité', icon: 'bx-check-shield', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
            { to: '/Security/Applications', label: 'Applications', icon: 'bx-package', roles: ['Users.Admin', 'Users.RSSI', 'Users.CDS'] },
        ],
    },
    {
        title: 'Administration',
        items: [
            // Nommées par leur contenu, comme le groupe Sécurité : une entrée
            // « Administration » sous un groupe « Administration » ne dit rien.
            { to: '/Administration', label: 'Rôles RSSI', icon: 'bx-user-check', end: true, roles: ['Users.Admin'] },
            { to: '/Administration/Jobs', label: 'Tâches planifiées', icon: 'bx-time-five', roles: ['Users.Admin'] },
            { to: '/Migration', label: 'Migration', icon: 'bx-data', roles: ['Users.Admin'] },
        ],
    },
];

const PAGE_TITLES = {
    '/': 'Accueil',
    '/Users': 'Utilisateurs',
    '/AdvancedSearch': 'Recherche avancée',
    '/Diff': 'Diff',
    '/Dashboard': 'Dashboard',
    '/AiQuery': 'Requête IA',
    '/Security': 'Sécurité',
    '/Security/Vulnerabilities': 'Vulnérabilités',
    '/Security/Devices': 'Appareils Defender',
    '/Security/Compliance': 'Conformité Intune',
    '/Security/Applications': 'Applications Intune',
    '/Administration': 'Rôles RSSI',
    '/Administration/Jobs': 'Tâches planifiées',
    '/Migration': 'Migration',
};

function roleBadge(roles) {
    if (hasRole(roles, ['Users.Admin'])) return { label: 'Admin', tone: 'accent' };
    if (hasRole(roles, ['Users.RSSI'])) return { label: 'RSSI', tone: 'rssi' };
    if (hasRole(roles, ['Users.CDS'])) return { label: 'CDS', tone: 'neutral' };
    if (hasRole(roles, ['Users.Basic'])) return { label: 'Basic', tone: 'neutral' };
    return null;
}

export default function AppShell({ roles, darkMode, toggleTheme, children }) {
    const { instance, accounts } = useMsal();
    const dispatch = useDispatch();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(
        () => localStorage.getItem('sidebarCollapsed') === 'true'
    );

    const toggleCollapsed = () => {
        setCollapsed(prev => {
            localStorage.setItem('sidebarCollapsed', String(!prev));
            return !prev;
        });
    };

    const handleLogout = () => {
        instance.logoutRedirect({ postLogoutRedirectUri: '/' });
        dispatch(setAuthReset());
    };

    const userName = accounts[0]?.name || '';
    const initials = userName
        .split(' ')
        .map(part => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();
    const badge = roleBadge(roles);
    const pageTitle = PAGE_TITLES[location.pathname] || 'Habilitations';

    return (
        <Shell $collapsed={collapsed}>
            <Sidebar $collapsed={collapsed}>
                <Brand>
                    <Link to="/">
                        <BrandLogo>C</BrandLogo>
                        <BrandName className="brand-name">
                            Habilitations
                            <small>Groupe Constellation</small>
                        </BrandName>
                    </Link>
                </Brand>

                {NAV_GROUPS.map(group => {
                    const visibleItems = group.items.filter(
                        item => !item.roles || hasRole(roles, item.roles)
                    );
                    if (visibleItems.length === 0) return null;
                    return (
                        <React.Fragment key={group.title}>
                            <NavGroupTitle className="nav-group-title">{group.title}</NavGroupTitle>
                            {visibleItems.map(item => (
                                <NavItem
                                    key={item.to}
                                    to={item.to}
                                    end={item.end || undefined}
                                    title={collapsed ? item.label : undefined}
                                >
                                    <i className={`bx ${item.icon}`} />
                                    <span className="nav-label">{item.label}</span>
                                </NavItem>
                            ))}
                        </React.Fragment>
                    );
                })}

                <SidebarFooter>
                    <UserCard>
                        <Avatar>{initials || <i className="bx bx-user" />}</Avatar>
                        <UserMeta className="user-meta">
                            <div className="name">{userName}</div>
                            {badge && <Pill $tone={badge.tone}>{badge.label}</Pill>}
                        </UserMeta>
                    </UserCard>
                </SidebarFooter>
            </Sidebar>

            <Main>
                <Topbar>
                    <IconButton onClick={toggleCollapsed} title={collapsed ? 'Déplier la navigation' : 'Replier la navigation'}>
                        <i className="bx bx-menu" />
                    </IconButton>
                    <h1>{pageTitle}</h1>
                    <TopbarSpacer />
                    <IconButton onClick={toggleTheme} title={darkMode ? 'Thème clair' : 'Thème sombre'}>
                        <i className={`bx ${darkMode ? 'bx-sun' : 'bx-moon'}`} />
                    </IconButton>
                    <IconButton onClick={handleLogout} title="Se déconnecter">
                        <i className="bx bx-log-out" />
                    </IconButton>
                </Topbar>
                <Content>{children}</Content>
            </Main>
        </Shell>
    );
}
